import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  DOCUMENT_CATEGORIES,
  PROMPT_FOR_MISSING_IMPORTANT_DOCUMENTS,
  UPLOAD_ACCEPT,
  UPLOAD_MAX_BYTES,
  categoryLabel,
  classifyClientDocument,
  completeClientDocumentUpload,
  fetchClientDocumentDownload,
  fetchClientDocuments,
  putFileToSignedUrl,
  startClientDocumentUpload,
} from "../helpers/documentsApi";
import { formatCreatedDate, formatFileSize } from "../helpers/utilityHelper";
import DocumentViewer, { viewerKind } from "./DocumentViewer";

const EMPTY = { categories: [], uncategorized: [] };

// Portuguese labels for Docrio tags that are not client categories (shown as their own group).
const TAG_LABELS_PT = {
  "Questionnaire 2019": "Questionário 2019",
  "Legal Document": "Documento jurídico",
  "Conga And SOCI": "Contrato e termos (SOCI)",
  Unknown: "Outros (não identificado)",
};

const groupLabel = (group) =>
  group.custom ? TAG_LABELS_PT[group.label] || group.label : categoryLabel(group.key);

const openSignedUrl = (url, pendingWindow) => {
  if (pendingWindow && !pendingWindow.closed) {
    pendingWindow.location.href = url;
    return;
  }
  const a = document.createElement("a");
  a.href = url;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  document.body.appendChild(a);
  a.click();
  a.remove();
};

// Moves a document into its group after a (re)classification or upload; no category -> "a categorizar".
const applyClassification = (data, updated) => {
  const without = (docs) => docs.filter((d) => d.id !== updated.id);
  return {
    uncategorized: updated.category
      ? without(data.uncategorized)
      : [updated, ...without(data.uncategorized)],
    categories: data.categories.map((group) => ({
      ...group,
      documents:
        group.key === updated.category
          ? [updated, ...without(group.documents)]
          : without(group.documents),
    })),
  };
};

const validateFile = (file) => {
  if (file.size > UPLOAD_MAX_BYTES) {
    return `"${file.name}" é maior que 20 MB. Escolha um arquivo menor.`;
  }
  const ext = (file.name.split(".").pop() || "").toLowerCase();
  if (!UPLOAD_ACCEPT.split(",").includes(`.${ext}`)) {
    return `"${file.name}": tipo de arquivo não aceito. Envie PDF, imagem (JPG, PNG, HEIC) ou Word.`;
  }
  return "";
};

function CategoryPicker({ value, disabled, onChange, label, categories }) {
  return (
    <select
      className="docsSelect"
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      aria-label={label}
    >
      <option value="">Escolha a categoria</option>
      {(categories || DOCUMENT_CATEGORIES).map((c) => (
        <option key={c.key} value={c.key}>
          {c.label}
        </option>
      ))}
    </select>
  );
}

function DocumentRow({
  doc,
  busy,
  editing,
  onView,
  onStartEdit,
  onCancelEdit,
  onSave,
  needsClassification,
  categories,
}) {
  const [choice, setChoice] = useState("");
  const showPicker = needsClassification || editing;
  const name = doc.name || "Documento";

  return (
    <div className="docsItem docsItem--stacked">
      <div className="docsItemRow">
        <div className="docsItemLeft">
          <div className="docsItemTitle">{name}</div>
          <div className="docsItemMeta">
            {formatCreatedDate(doc.createdDate || doc.lastModifiedDate)}
            {doc.fileSize ? ` • ${formatFileSize(doc.fileSize)}` : ""}
          </div>
        </div>
        <div className="docsItemActions">
          <button
            type="button"
            className="docsAction"
            onClick={() => onView(doc)}
            disabled={busy}
            aria-label={`Visualizar documento ${name}`}
          >
            Visualizar
          </button>
          {!needsClassification && !editing ? (
            <button
              type="button"
              className="docsAction docsAction--ghost"
              onClick={() => onStartEdit(doc.id)}
              disabled={busy}
              aria-label={`Alterar categoria de ${name}`}
            >
              Alterar categoria
            </button>
          ) : null}
        </div>
      </div>

      {showPicker ? (
        <div className="docsClassify">
          <CategoryPicker
            value={choice}
            disabled={busy}
            onChange={setChoice}
            label={`Categoria de ${name}`}
            categories={categories}
          />
          <button
            type="button"
            className="docsAction docsAction--primary"
            disabled={busy || !choice || choice === doc.category}
            onClick={() => onSave(doc, choice)}
            aria-label={`Salvar categoria de ${name}`}
          >
            {busy ? "Salvando..." : "Salvar"}
          </button>
          {editing ? (
            <button
              type="button"
              className="docsBackBtn"
              onClick={onCancelEdit}
              disabled={busy}
            >
              Cancelar
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function UploadButton({ slot, category, label, text = "Enviar", uploading, progress, disabled, onFiles, ghost }) {
  const inputId = `docs-upload-${slot}`;
  return (
    <>
      <input
        id={inputId}
        type="file"
        multiple
        accept={UPLOAD_ACCEPT}
        className="docsFileInput"
        aria-label={`Arquivo para ${label}`}
        disabled={disabled}
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          e.target.value = "";
          if (files.length) onFiles(slot, category, files);
        }}
      />
      <label
        htmlFor={inputId}
        className={`docsAction ${ghost ? "docsAction--ghost" : "docsAction--primary"} docsUploadLabel${
          disabled ? " is-disabled" : ""
        }`}
        aria-label={`Enviar ${label}`}
      >
        {uploading ? `Enviando... ${progress}%` : text}
      </label>
    </>
  );
}

// Wraps a card so files can be dropped onto it; the drop uploads into that card's category.
function DropZone({ slot, category, disabled, onFiles, className, label, children }) {
  const [over, setOver] = useState(false);
  const depth = useRef(0);
  const accepts = (e) => !disabled && Array.from(e.dataTransfer?.types || []).includes("Files");

  return (
    <div
      className={`${className}${over ? " is-dragover" : ""}`}
      aria-label={label}
      onDragEnter={(e) => {
        if (!accepts(e)) return;
        e.preventDefault();
        depth.current += 1;
        setOver(true);
      }}
      onDragOver={(e) => {
        if (!accepts(e)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
      }}
      onDragLeave={() => {
        depth.current = Math.max(0, depth.current - 1);
        if (depth.current === 0) setOver(false);
      }}
      onDrop={(e) => {
        if (disabled) return;
        e.preventDefault();
        depth.current = 0;
        setOver(false);
        const files = Array.from(e.dataTransfer?.files || []);
        if (files.length) onFiles(slot, category, files);
      }}
    >
      {children}
      {over ? <div className="docsDropHint">Solte o arquivo para enviar</div> : null}
    </div>
  );
}

// Collapsible section header: the title area toggles the section; actions (badges, upload) stay clickable.
function SectionHeader({ open, onToggle, controls, title, icon, children, headerRef }) {
  return (
    <div className="docsGroupHeader" ref={headerRef}>
      <button type="button" className="docsGroupToggle" aria-expanded={open} aria-controls={controls} onClick={onToggle}>
        <span className={`docsChevron${open ? " is-open" : ""}`} aria-hidden="true" />
        {icon}
        <span className="docsGroupTitle">{title}</span>
      </button>
      <span className="docsMissingActions">{children}</span>
    </div>
  );
}

const UNCATEGORIZED_KEY = "UNCATEGORIZED";
const UNCATEGORIZED_TITLE = "Documentos a categorizar";
const CATEGORIZED_TITLE = "Documentos classificados";
const isMobileViewport = () =>
  typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(max-width: 680px)").matches;

export default function ClientDocumentsSection({ idToken, partyId, matterId }) {
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busyId, setBusyId] = useState("");
  const [editingId, setEditingId] = useState("");
  const [upload, setUpload] = useState({ slot: "", progress: 0 });
  const [genericCategory, setGenericCategory] = useState("");
  const [viewer, setViewer] = useState(null);
  const closeViewer = useCallback(() => setViewer(null), []);
  // Sections start collapsed on mobile, open on desktop; sections needing action always start open.
  const [mobile] = useState(isMobileViewport);
  const [openOverrides, setOpenOverrides] = useState({});
  const isOpen = (key, needsAction) => (key in openOverrides ? openOverrides[key] : needsAction || !mobile);
  const setOpen = (key, value) => setOpenOverrides((o) => ({ ...o, [key]: value }));
  const firstMissingRef = useRef(null);

  useEffect(() => {
    if (!idToken || !partyId || !matterId) {
      setData(EMPTY);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError("");
    fetchClientDocuments(idToken, partyId, matterId)
      .then((resp) => {
        if (cancelled) return;
        setData({
          categories: Array.isArray(resp?.categories) ? resp.categories : [],
          uncategorized: Array.isArray(resp?.uncategorized) ? resp.uncategorized : [],
        });
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Failed to load client documents:", err);
        setData(EMPTY);
        setError("Não foi possível carregar seus documentos. Tente novamente mais tarde.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [idToken, partyId, matterId]);

  const handleFiles = useCallback(
    async (slot, category, files) => {
      setError("");
      setNotice("");
      const invalid = files.map(validateFile).find(Boolean);
      if (invalid) {
        setError(invalid);
        return;
      }
      const sent = [];
      for (const file of files) {
        setUpload({ slot, progress: 0 });
        try {
          const started = await startClientDocumentUpload(idToken, partyId, matterId, file, category || null);
          await putFileToSignedUrl(started.uploadUrl, file, (progress) => setUpload({ slot, progress }));
          const completed = await completeClientDocumentUpload(idToken, partyId, matterId, started.documentId);
          const doc = {
            fileSize: file.size,
            createdDate: new Date().toISOString(),
            ...completed,
            category: category || null,
          };
          setData((current) => applyClassification(current, doc));
          setOpenOverrides((o) => ({ ...o, [category || UNCATEGORIZED_KEY]: true }));
          sent.push(file.name);
        } catch (err) {
          console.error("Failed to upload document:", err);
          setError(`Não foi possível enviar "${file.name}". Tente novamente.`);
          break;
        }
      }
      setUpload({ slot: "", progress: 0 });
      if (sent.length) {
        const what = sent.length === 1 ? `"${sent[0]}" foi enviado` : `${sent.length} arquivos foram enviados`;
        setNotice(
          category
            ? `${what} em ${categoryLabel(category)}. Obrigado!`
            : `${what} e está em "${UNCATEGORIZED_TITLE}". Obrigado!`,
        );
        if (slot === "generic") setGenericCategory("");
      }
    },
    [idToken, partyId, matterId],
  );

  const handleView = useCallback(
    async (doc) => {
      setError("");
      const kind = viewerKind(doc);
      setBusyId(doc.id);
      const pendingWindow = kind ? null : window.open("", "_blank");
      // Open the viewer at once so the client sees progress while the signed URL is requested.
      if (kind) setViewer({ doc, url: null });
      try {
        const resp = await fetchClientDocumentDownload(idToken, partyId, matterId, doc.id);
        const url = resp?.signedUrlV2 || resp?.signedUrl;
        if (!url) throw new Error("missing signed url");
        if (kind) setViewer((v) => (v && v.doc.id === doc.id ? { doc, url } : v));
        else openSignedUrl(url, pendingWindow);
      } catch (err) {
        console.error("Failed to open document:", err);
        if (pendingWindow && !pendingWindow.closed) pendingWindow.close();
        if (kind) setViewer((v) => (v && v.doc.id === doc.id ? null : v));
        setError(
          err?.status === 404
            ? "Este arquivo não está disponível no momento. Fale conosco se precisar dele."
            : "Não foi possível abrir o documento. Tente novamente.",
        );
      } finally {
        setBusyId("");
      }
    },
    [idToken, partyId, matterId],
  );

  const handleSave = useCallback(
    async (doc, category) => {
      setError("");
      setNotice("");
      setBusyId(doc.id);
      try {
        const updated = await classifyClientDocument(idToken, partyId, matterId, doc.id, category);
        setData((current) => applyClassification(current, { ...doc, ...updated, category }));
        setOpenOverrides((o) => ({ ...o, [category]: true }));
        setEditingId("");
        setNotice(`"${doc.name || "Documento"}" foi classificado como ${categoryLabel(category)}.`);
      } catch (err) {
        console.error("Failed to classify document:", err);
        setError("Não foi possível salvar a categoria. Tente novamente.");
      } finally {
        setBusyId("");
      }
    },
    [idToken, partyId, matterId],
  );

  // Only categories the backend returns are offered (e.g. no Power of Attorney without a co-counsel, TDDS-3407).
  const { fixedGroups, tagGroups } = useMemo(() => {
    const byKey = new Map(data.categories.map((g) => [g.key, g]));
    return {
      fixedGroups: DOCUMENT_CATEGORIES.filter((c) => byKey.has(c.key)).map((c) => ({
        ...c,
        expected: byKey.get(c.key)?.expected ?? c.key !== "OTHER",
        mandatory: Boolean(byKey.get(c.key)?.mandatory),
        documents: byKey.get(c.key)?.documents || [],
      })),
      tagGroups: data.categories.filter((g) => g.custom && (g.documents || []).length > 0),
    };
  }, [data.categories]);
  const allowedCategories = useMemo(
    () => fixedGroups.map(({ key, label }) => ({ key, label })),
    [fixedGroups],
  );

  const mandatoryGroups = fixedGroups.filter((g) => g.mandatory);
  const missingMandatory = mandatoryGroups.filter((g) => g.documents.length === 0);
  const totalDocuments =
    data.uncategorized.length +
    fixedGroups.reduce((n, g) => n + g.documents.length, 0) +
    tagGroups.reduce((n, g) => n + g.documents.length, 0);

  if (!partyId || !matterId) return null;

  const busyUpload = Boolean(upload.slot);
  const rowProps = (doc, needsClassification) => ({
    doc,
    needsClassification,
    busy: busyId === doc.id,
    editing: editingId === doc.id,
    onView: handleView,
    onStartEdit: setEditingId,
    onCancelEdit: () => setEditingId(""),
    onSave: handleSave,
    categories: allowedCategories,
  });

  const renderFixedGroup = (group) => {
    const empty = group.documents.length === 0;
    // Important documents are advisory (TDDS-3492): a missing one is styled like any other missing document.
    const missingRequired = empty && group.mandatory;
    const slot = `cat-${group.key}`;
    const open = isOpen(group.key, missingRequired);
    const bodyId = `docs-body-${group.key}`;
    return (
      <DropZone
        key={group.key}
        slot={slot}
        category={group.key}
        disabled={busyUpload}
        onFiles={handleFiles}
        label={group.label}
        className={`docsGroup${empty ? " docsGroup--empty" : ""}${open ? "" : " is-collapsed"}`}
      >
        <SectionHeader
          open={open}
          onToggle={() => setOpen(group.key, !open)}
          controls={bodyId}
          title={group.label}
          headerRef={missingRequired && missingMandatory[0]?.key === group.key ? firstMissingRef : undefined}
        >
            {group.mandatory ? <span className="docsBadge docsBadge--mandatory">Importante</span> : null}
            {empty ? (
              missingRequired || group.expected ? (
                <span className="docsBadge docsBadge--missing">Não recebido</span>
              ) : null
            ) : (
              <span className="docsBadge" aria-label={`${group.documents.length} documentos`}>{group.documents.length}</span>
            )}
            <UploadButton
              slot={slot}
              category={group.key}
              label={empty ? group.label : `mais ${group.label}`}
              text={empty ? "Enviar" : "Adicionar"}
              ghost={!empty}
              uploading={upload.slot === slot}
              progress={upload.progress}
              disabled={busyUpload}
              onFiles={handleFiles}
            />
        </SectionHeader>
        {!open ? null : empty ? (
          <p className="docsMuted" id={bodyId}>
            {missingRequired
              ? "Este documento é importante para o seu processo. Envie quando puder, aqui ou arrastando o arquivo para este quadro."
              : group.key === "OTHER"
                ? "Nenhum outro documento por enquanto."
                : "Ainda não recebemos este documento. Se você o tiver, envie-o aqui."}
          </p>
        ) : (
          <div className="docsGroupBody" id={bodyId}>
            {group.documents.map((doc) => <DocumentRow key={doc.id} {...rowProps(doc, false)} />)}
          </div>
        )}
      </DropZone>
    );
  };

  const allSectionKeys = [
    ...fixedGroups.map((g) => g.key),
    ...tagGroups.map((g) => g.key),
  ];
  const setAll = (value) => setOpenOverrides(Object.fromEntries(allSectionKeys.map((k) => [k, value])));

  return (
    <section className="docsClient" aria-label="Meus documentos">
      {viewer ? <DocumentViewer doc={viewer.doc} url={viewer.url} onClose={closeViewer} /> : null}
      <div className="docsSectionTitle">Meus documentos</div>
      <p className="docsMuted">Seus documentos organizados por tipo.</p>

      {PROMPT_FOR_MISSING_IMPORTANT_DOCUMENTS && !loading && missingMandatory.length > 0 ? (
        <div className="docsRequiredBanner" role="alert" aria-label="Documentos importantes pendentes">
          <span className="docsRequiredBannerBadge">Atenção</span>
          <div className="docsRequiredBannerTitle">Ainda precisamos de alguns documentos</div>
          <div className="docsRequiredBannerText">
            Estes documentos são importantes para o seu processo:{" "}
            <strong>{missingMandatory.map((g) => g.label).join(", ")}</strong>. Envie quando puder, aqui abaixo.
          </div>
          <button
            type="button"
            className="docsRequiredBannerCta"
            onClick={() => firstMissingRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })}
          >
            Enviar documentos
          </button>
        </div>
      ) : null}

      {!loading && totalDocuments > 0 ? (
        <div className="docsReviewTip" role="note">
          <strong>Confira seus documentos.</strong> Alguns podem estar sem categoria ou na categoria
          errada. Abra cada documento e, se necessário, use “Alterar categoria” para corrigir.
        </div>
      ) : null}

      {error ? (
        <div className="docsError" role="alert">
          {error}
        </div>
      ) : null}
      {notice ? (
        <div className="docsNotice" role="status">
          {notice}
        </div>
      ) : null}

      {loading ? (
        <div className="docsMuted">Carregando documentos...</div>
      ) : (
        <>
          {mandatoryGroups.length > 0 ? (
            <div className="docsSummary">
              {mandatoryGroups.length - missingMandatory.length} de {mandatoryGroups.length} documentos
              importantes enviados
            </div>
          ) : null}

          <DropZone
            slot="generic"
            category={genericCategory}
            disabled={busyUpload}
            onFiles={handleFiles}
            label="Área para enviar documentos"
            className="docsGroup docsGroup--upload docsDropArea"
          >
            <div className="docsGroupHeader">
              <span className="docsGroupTitle">Enviar documentos</span>
            </div>
            <p className="docsMuted">
              Arraste e solte arquivos aqui ou escolha no seu dispositivo (PDF, imagem ou Word, até
              20 MB cada). Se souber, escolha o tipo antes de enviar.
            </p>
            <div className="docsClassify">
              <select
                className="docsSelect"
                value={genericCategory}
                disabled={busyUpload}
                onChange={(e) => setGenericCategory(e.target.value)}
                aria-label="Tipo do documento a enviar"
              >
                <option value="">Não sei / sem categoria</option>
                {allowedCategories.map((c) => (
                  <option key={c.key} value={c.key}>
                    {c.label}
                  </option>
                ))}
              </select>
              <UploadButton
                slot="generic"
                category={genericCategory}
                label="outro documento"
                text="Escolher arquivo"
                uploading={upload.slot === "generic"}
                progress={upload.progress}
                disabled={busyUpload}
                onFiles={handleFiles}
              />
            </div>
          </DropZone>

          {/* TDDS-3486: files still needing a category get their own block, separate from classified ones. */}
          {data.uncategorized.length > 0 ? (
            <section className="docsBlock docsBlock--review" aria-labelledby="docs-heading-uncategorized">
              <div className="docsBlockHeader">
                <h3 className="docsBlockTitle" id="docs-heading-uncategorized">
                  {UNCATEGORIZED_TITLE}
                </h3>
                <span className="docsBadge docsBadge--review" aria-label={`${data.uncategorized.length} documentos`}>
                  {data.uncategorized.length}
                </span>
              </div>
              <p className="docsBlockIntro">
                Ainda não sabemos o tipo destes documentos. Abra cada arquivo, escolha a categoria correta e
                clique em “Salvar”. O documento passa para “{CATEGORIZED_TITLE}”.
              </p>
              <div className="docsGroupBody">
                {data.uncategorized.map((doc) => (
                  <DocumentRow key={doc.id} {...rowProps(doc, true)} />
                ))}
              </div>
            </section>
          ) : null}

          {fixedGroups.length > 0 || tagGroups.length > 0 ? (
            <section className="docsBlock" aria-labelledby="docs-heading-categorized">
              <div className="docsBlockHeader">
                <h3 className="docsBlockTitle" id="docs-heading-categorized">
                  {CATEGORIZED_TITLE}
                </h3>
              </div>
              <div className="docsToolbar">
                <span className="docsToolbarLabel">Separados por tipo de documento</span>
                <button type="button" className="docsToolbarBtn" onClick={() => setAll(true)}>
                  Expandir tudo
                </button>
                <button type="button" className="docsToolbarBtn" onClick={() => setAll(false)}>
                  Recolher tudo
                </button>
              </div>

              {fixedGroups.map(renderFixedGroup)}

              {tagGroups.map((group) => {
                const open = isOpen(group.key, false);
                const bodyId = `docs-body-${group.key.replace(/[^A-Za-z0-9_-]/g, "_")}`;
                return (
                  <div key={group.key} className={`docsGroup${open ? "" : " is-collapsed"}`}>
                    <SectionHeader open={open} onToggle={() => setOpen(group.key, !open)} controls={bodyId} title={groupLabel(group)}>
                      <span className="docsBadge">{group.documents.length}</span>
                    </SectionHeader>
                    {open ? (
                      <div className="docsGroupBody" id={bodyId}>
                        {group.documents.map((doc) => (
                          <DocumentRow key={doc.id} {...rowProps(doc, false)} />
                        ))}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </section>
          ) : null}
        </>
      )}
    </section>
  );
}
